import { formatPropertyAddress } from '../utils/propertyAddress';
import { propertyFetch } from '../services/idxPropertyClient';
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { SEO_CONFIG } from '../config/seo';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import './IDXTownPage.css';
import SEOLinks from './SEOLinks';

const HuntingtonBeachVietnameseCommunityPage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [propertyImages, setPropertyImages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, [isDarkMode]);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary?city=Huntington+Beach&firstPhoto=true&hasMedia=true&limit=9`, {});
        if (response.ok) {
          const data = await response.json();
          const props = data.properties || [];
          setProperties(props);
          props.forEach(p => {
            if (p.listing_key) {
              propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-media/${p.listing_key}`, {}).then(r => r.json()).then(d => {
                const first = (d.media || []).sort((a, b) => parseInt(a.order_hint) - parseInt(b.order_hint))[0];
                const url = first?.media_url || first?.s3_link;
                if (url) setPropertyImages(prev => ({ ...prev, [p.listing_key]: url }));
              }).catch(() => {});
            }
          });
        }
      } catch (error) {
        console.error('Error fetching HB Vietnamese community properties:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, []);

  return (
    <div className="idxtown-page">
      <Helmet>
        <title>Huntington Beach Vietnamese Community Homes | Truc Doan</title>
        <meta name="description" content="Find homes in Huntington Beach's Vietnamese community. Enjoy beach lifestyle living with proximity to Little Saigon. Upscale Vietnamese neighborhoods with excellent schools. Contact Truc Doan." />
        <meta name="keywords" content="Huntington Beach Vietnamese community homes, Vietnamese neighborhood Huntington Beach, HB homes near Little Saigon, Vietnamese realtor Huntington Beach, beach homes Vietnamese community, upscale Vietnamese neighborhoods Orange County, Truc Doan Huntington Beach" />
        <link rel="canonical" href={`${SEO_CONFIG.DOMAIN}/huntington-beach-vietnamese-community-homes`} />
        <meta property="og:title" content="Huntington Beach Vietnamese Community Homes | Truc Doan" />
        <meta property="og:description" content="Discover homes in Huntington Beach's thriving Vietnamese community. Beach lifestyle meets cultural richness near Little Saigon." />
        <meta property="og:url" content={`${SEO_CONFIG.DOMAIN}/huntington-beach-vietnamese-community-homes`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={SEO_CONFIG.OG_IMAGE} />
        <meta name="geo.region" content="US-CA" />
        <meta name="geo.placename" content="Huntington Beach, California" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            "name": "Truc Doan",
            "telephone": SEO_CONFIG.AGENT_PHONE,
            "email": SEO_CONFIG.AGENT_EMAIL,
            "url": `${SEO_CONFIG.DOMAIN}/huntington-beach-vietnamese-community-homes`,
            "image": SEO_CONFIG.OG_IMAGE,
            "description": "Truc Doan helps Vietnamese families find homes in Huntington Beach. Enjoy beach lifestyle living with proximity to Little Saigon and upscale Vietnamese neighborhoods.",
            "areaServed": {
              "@type": "City",
              "name": "Huntington Beach, California"
            },
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Westminster",
              "addressRegion": "CA",
              "postalCode": "92683",
              "addressCountry": "US"
            }
          })}
        </script>
      </Helmet>

      <div className="idxtown-container">
        <button onClick={() => navigate('/')} style={{ position: 'absolute', top: '20px', left: '20px', padding: '10px 20px', background: 'rgb(14, 165, 233)', color: 'rgb(243, 244, 246)', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', zIndex: 10 }}>
          {t('nav.home')}
        </button>

        <header className="idxtown-header">
          <h1>Huntington Beach Vietnamese Community Homes</h1>
          <p style={{ marginTop: '16px', fontSize: '18px', opacity: 0.9 }}>
            Beach lifestyle living with the warmth of Vietnamese community — the best of both worlds in Huntington Beach.
          </p>
        </header>

        <section className="idxtown-vibe">
          <h2>Vietnamese Community in Huntington Beach</h2>
          <p>Huntington Beach has become one of the most desirable locations for Vietnamese-American families seeking an upscale beach lifestyle while staying connected to their cultural roots. With Little Saigon just minutes away in Westminster and Garden Grove, HB residents enjoy easy access to Vietnamese restaurants, markets, temples, and community events.</p>
          <p>The Vietnamese community in Huntington Beach has grown significantly, with many families upgrading from Westminster and Garden Grove to enjoy larger homes, better schools, and the iconic beach lifestyle that Surf City USA offers.</p>
        </section>

        <section className="idxtown-lifestyle">
          <h2>Why Vietnamese Families Choose Huntington Beach</h2>
          <p>Huntington Beach offers a unique combination of beach living and cultural accessibility that's hard to find anywhere else.</p>
          <div className="amenities-list">
            <div className="amenity">
              <h3>Beach Lifestyle</h3>
              <p>Miles of pristine coastline, world-class surfing, beach bonfires, and the iconic HB Pier create an unmatched quality of life for families and professionals.</p>
            </div>
            <div className="amenity">
              <h3>Proximity to Little Saigon</h3>
              <p>Just 10-15 minutes from the heart of Little Saigon, residents enjoy authentic Vietnamese cuisine, cultural festivals, and community without sacrificing beach living.</p>
            </div>
            <div className="amenity">
              <h3>Upscale Vietnamese Neighborhoods</h3>
              <p>Areas like Seacliff, Huntington Harbour, and Edwards Hill feature luxury homes popular with Vietnamese-American families seeking premium living.</p>
            </div>
            <div className="amenity">
              <h3>Top-Rated Schools</h3>
              <p>Huntington Beach Union High School District and Ocean View School District offer excellent educational opportunities, a top priority for Vietnamese families.</p>
            </div>
            <div className="amenity">
              <h3>Growing Community</h3>
              <p>Vietnamese-owned businesses, medical practices, and professional services are thriving in Huntington Beach, creating a supportive community network.</p>
            </div>
          </div>
        </section>

        <section className="market-snapshot">
          <h2>Huntington Beach Market Snapshot</h2>
          <p>The Huntington Beach real estate market is competitive, with strong demand from Vietnamese-American buyers seeking beach proximity.</p>
          <table className="market-stats">
            <thead>
              <tr>
                <th>Market Stat</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>Median Home Price</td><td>~$1,200,000</td></tr>
              <tr><td>Average Days on Market</td><td>25–35 Days</td></tr>
              <tr><td>Most Popular Price Range</td><td>$900K – $1.5M</td></tr>
              <tr><td>Popular Neighborhoods</td><td>Seacliff, Huntington Harbour, Edwards Hill</td></tr>
              <tr><td>Distance to Little Saigon</td><td>10–15 minutes</td></tr>
            </tbody>
          </table>
        </section>

        <section className="properties-section">
          <h2>Huntington Beach Homes Currently for Sale</h2>
          {loading ? (
            <p style={{ color: 'rgb(156, 163, 175)' }}>{t('property.loadingProperties')}</p>
          ) : properties.length > 0 ? (
            <div className="properties-grid">
              {properties.slice(0, 9).map((property) => (
                <div key={property.listing_key} className="property-card" onClick={() => navigate(`/listing/${property.listing_key}`)} style={{ backgroundImage: (propertyImages[property.listing_key] || property.first_photo_url) ? `url(${propertyImages[property.listing_key] || property.first_photo_url})` : 'none', backgroundSize: 'cover', backgroundPosition: 'center' }}>
                  <div className="property-price">${property.current_price?.toLocaleString()}</div>
                  <div className="property-address">{formatPropertyAddress(property)}, CA {property.postal_code}</div>
                  <div className="property-type">{property.property_sub_type}</div>
                  {(property.list_agent_full_name || property.list_office_name) && (
                    <div style={{fontSize: '11px', color: '#ddd', marginTop: '2px'}}>{t('landing.listedBy')}: {[property.list_agent_full_name, property.list_office_name].filter(Boolean).join(' | ')}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'rgb(156, 163, 175)' }}>No properties currently listed. Contact Truc Doan for exclusive Huntington Beach listings.</p>
          )}
        </section>

        <section className="cta-section">
          <h2>Find Your Dream Home in Huntington Beach</h2>
          <p>As a Vietnamese-American realtor who understands both cultures, Truc Doan can help you find the perfect Huntington Beach home near the Vietnamese community. Bilingual service available.</p>
          <div className="cta-buttons">
            <a href={`mailto:${SEO_CONFIG.AGENT_EMAIL}?subject=Huntington Beach Vietnamese Community Home Inquiry`} className="cta-button primary">{t('county.emailAgent')}</a>
            <a href={`tel:${SEO_CONFIG.AGENT_PHONE}`} className="cta-button secondary">{t('county.callAgent', { phone: SEO_CONFIG.AGENT_PHONE })}</a>
            <button className="cta-button primary" onClick={() => navigate('/chat')} style={{ border: 'none', cursor: 'pointer' }}>Search HB Listings</button>
          </div>
        </section>
      </div>

      <div style={{ background: 'rgba(0,0,0,0.85)', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/crmlslogo.png" alt="CRMLS" style={{ height: '20px', objectFit: 'contain' }} />
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/equalhousinglogo.png" alt="Equal Housing Opportunity" style={{ height: '20px', objectFit: 'contain' }} />
        <span style={{ fontSize: '10px', color: '#ccc', flex: 1 }}>Based on information from California Regional Multiple Listing Service, Inc. and/or other sources. All data, including all measurements and calculations of area, is obtained from various sources and has not been, and will not be, verified by broker or MLS. All information should be independently reviewed and verified for accuracy. Properties may or may not be listed by the office/agent presenting the information.</span>
      </div>
      <SEOLinks />
    </div>
  );
};

export default HuntingtonBeachVietnameseCommunityPage;
